import { apiClient } from '@/services/api-client';
import {
  ProviderApiAccessOverview,
  ProviderApiToken,
  ProviderApiTokenCreateInput,
  ProviderApiTokenCreateResponse,
  ProviderPortalLinkPayload,
  ProviderPortalLinkResponse,
  ProviderSsoConfig,
} from '@/types/provider-api-token';

interface ProviderApiTokenResource {
  id: string;
  attributes?: Record<string, unknown>;
  [key: string]: unknown;
}

interface ProviderApiTokensApiPayload {
  data?: ProviderApiTokenResource[] | ProviderApiTokenResource;
  meta?: Record<string, unknown>;
  tokens?: ProviderApiTokenResource[];
  sso_config?: Record<string, unknown>;
  ssoConfig?: Record<string, unknown>;
}

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

const readStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];

const readDate = (value: unknown): string | null => {
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }
  return null;
};

const readNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && !Number.isNaN(value) ? value : undefined;

function normalizeToken(resource: ProviderApiTokenResource): ProviderApiToken {
  const attributes =
    resource.attributes && typeof resource.attributes === 'object'
      ? (resource.attributes as Record<string, unknown>)
      : resource;

  return {
    id: readString(resource.id) ?? readString(attributes.id) ?? crypto.randomUUID(),
    label: readString(attributes.label) ?? 'Untitled token',
    scopes: readStringArray(attributes.scopes),
    createdAt: readString(attributes.created_at ?? attributes.createdAt) ?? new Date().toISOString(),
    lastUsedAt: readDate(attributes.last_used_at ?? attributes.lastUsedAt),
    expiresAt: readDate(attributes.expires_at ?? attributes.expiresAt) ?? undefined,
    status: (readString(attributes.status) as ProviderApiToken['status']) ?? 'active',
  };
}

function normalizeTokenCollection(payload: ProviderApiTokensApiPayload | ProviderApiTokenResource[]): ProviderApiToken[] {
  if (Array.isArray(payload)) {
    return payload.map(normalizeToken);
  }

  if (Array.isArray(payload.tokens)) {
    return payload.tokens.map(normalizeToken);
  }

  if (Array.isArray(payload.data)) {
    return payload.data.map(normalizeToken);
  }

  if (payload.data && typeof payload.data === 'object') {
    return [normalizeToken(payload.data as ProviderApiTokenResource)];
  }

  return [];
}

function normalizeSsoConfig(config: Record<string, unknown> | undefined): ProviderSsoConfig | null {
  if (!config) {
    return null;
  }

  const defaultTtlMinutes =
    readNumber(config.default_ttl_minutes ?? config.defaultTtlMinutes) ?? 15;
  const maxTtlMinutes = readNumber(config.max_ttl_minutes ?? config.maxTtlMinutes);
  const allowedRedirectDomains = readStringArray(
    config.allowed_redirect_domains ?? config.allowedRedirectDomains,
  );
  const overridesAllowed =
    typeof config.overrides_allowed === 'boolean'
      ? config.overrides_allowed
      : typeof config.overridesAllowed === 'boolean'
        ? config.overridesAllowed
        : false;

  return {
    defaultTtlMinutes,
    maxTtlMinutes,
    allowedRedirectDomains,
    overridesAllowed,
  };
}

class ProviderApiTokensService {
  async list(): Promise<ProviderApiAccessOverview> {
    const response = await apiClient.get<ProviderApiTokensApiPayload>('/api/v1/provider/api-tokens');
    const payload = response.data ?? {};
    const tokens = normalizeTokenCollection(payload);
    const ssoConfig = normalizeSsoConfig(
      (payload as Record<string, unknown>).sso_config as Record<string, unknown> | undefined ??
        (payload as Record<string, unknown>).ssoConfig as Record<string, unknown> | undefined,
    );

    return { tokens, ssoConfig: ssoConfig ?? null };
  }

  async create(input: ProviderApiTokenCreateInput): Promise<ProviderApiTokenCreateResponse> {
    const payload: Record<string, unknown> = {
      label: input.label,
      scopes: input.scopes,
    };

    if (typeof input.expiresInMinutes === 'number') {
      payload.expires_in_minutes = input.expiresInMinutes;
    }

    const response = await apiClient.post<ProviderApiTokenCreateResponse | Record<string, unknown>>(
      '/api/v1/provider/api-tokens',
      payload,
    );

    const envelope = (response.data ?? {}) as Record<string, unknown>;
    const responseData =
      (envelope.data as Record<string, unknown> | undefined) ?? envelope;

    const token = typeof responseData.token === 'string' ? responseData.token : undefined;

    if (!token) {
      throw new Error('Token issuance failed: no token returned');
    }

    const tokenId =
      typeof responseData.tokenId === 'string'
        ? responseData.tokenId
        : typeof responseData['token_id'] === 'string'
          ? (responseData['token_id'] as string)
          : crypto.randomUUID();

    const expiresAt =
      typeof responseData.expiresAt === 'string'
        ? responseData.expiresAt
        : typeof responseData['expires_at'] === 'string'
          ? (responseData['expires_at'] as string)
          : null;

    const scopes = Array.isArray(responseData.scopes)
      ? responseData.scopes.filter((item): item is string => typeof item === 'string')
      : input.scopes;

    return {
      token,
      tokenId,
      expiresAt,
      scopes,
    };
  }

  async revoke(tokenId: string): Promise<void> {
    await apiClient.delete(`/api/v1/provider/api-tokens/${tokenId}`);
  }

  async getSsoConfig(): Promise<ProviderSsoConfig | null> {
    const response = await apiClient.get<{ data?: Record<string, unknown> }>(
      '/api/v1/provider/api-tokens/config',
    );

    const config =
      (response.data?.data as Record<string, unknown> | undefined) ??
      ((response.data as unknown) as Record<string, unknown> | undefined);

    return normalizeSsoConfig(config) ?? null;
  }

  async issueCustomerPortalLink(
    payload: ProviderPortalLinkPayload,
  ): Promise<ProviderPortalLinkResponse> {
    const body: Record<string, unknown> = {};
    if (typeof payload.ttlMinutes === 'number') {
      body.ttl = payload.ttlMinutes;
    }
    if (payload.redirectUrl) {
      body.return_url = payload.redirectUrl;
    }

    const response = await apiClient.post<ProviderPortalLinkResponse | Record<string, unknown>>(
      `/api/v1/provider/customers/${payload.customerId}/sso-link`,
      Object.keys(body).length > 0 ? body : undefined,
    );

    const envelope = (response.data ?? {}) as Record<string, unknown>;
    const raw =
      (envelope.data as Record<string, unknown> | undefined) ?? envelope;
    const url = typeof raw.url === 'string' ? raw.url : undefined;
    if (typeof url !== 'string') {
      throw new Error('SSO link response missing URL.');
    }

    const expiresAt =
      (typeof raw.expiresAt === 'string' ? raw.expiresAt : undefined) ??
      (typeof raw['expires_at'] === 'string' ? (raw['expires_at'] as string) : undefined) ??
      null;
    const tokenId =
      (typeof raw.tokenId === 'string' ? raw.tokenId : undefined) ??
      (typeof raw['token_id'] === 'string' ? (raw['token_id'] as string) : undefined);

    return {
      url,
      expiresAt: typeof expiresAt === 'string' ? expiresAt : null,
      tokenId: typeof tokenId === 'string' ? tokenId : undefined,
    };
  }
}

export const providerApiTokensService = new ProviderApiTokensService();
